import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Loader2,
  XCircle,
  ArrowRight,
  AlertCircle,
} from "lucide-react";

const TERMINAL_OK = ["succeeded"];
const TERMINAL_FAIL = ["failed", "refunded"];

export default function PaymentReturn() {
  const [sp] = useSearchParams();
  const provider = sp.get("provider") || "stripe";
  const paymentId = sp.get("payment_id");
  const intent = sp.get("status") || "success"; // success | cancel

  const [status, setStatus] = useState("polling"); // polling | ok | fail | timeout | cancelled
  const [payment, setPayment] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (intent === "cancel") {
      setStatus("cancelled");
      return;
    }
    if (!paymentId) {
      setStatus("fail");
      return;
    }
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const list = await api.get(`/student/payments`).then((r) => r.data);
        const p = list.find((x) => x.id === paymentId);
        if (p) {
          setPayment(p);
          if (TERMINAL_OK.includes(p.status)) {
            setStatus("ok");
            clearInterval(pollRef.current);
            return;
          }
          if (TERMINAL_FAIL.includes(p.status)) {
            setStatus("fail");
            clearInterval(pollRef.current);
            return;
          }
        }
        if (tries >= 30) {
          setStatus("timeout");
          clearInterval(pollRef.current);
        }
      } catch {
        // keep polling
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => clearInterval(pollRef.current);
  }, [paymentId, intent]);

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="payment-return"
    >
      <DashboardHeader subtitle="Payment status" />
      <main className="mx-auto max-w-xl px-6 py-16">
        <BackButton to="/student" label="My dashboard" className="mb-3" />
        <Card className="border-stone-200">
          <CardContent className="py-10 text-center space-y-4">
            {status === "polling" && (
              <>
                <Loader2 className="mx-auto h-10 w-10 animate-spin text-amber-600" />
                <h1 className="text-xl font-semibold">
                  Confirming your payment…
                </h1>
                <p className="text-sm text-stone-500">
                  Hang tight — {provider === "stripe" ? "Stripe" : "Razorpay"}{" "}
                  is sending us the confirmation. This usually takes a few
                  seconds.
                </p>
              </>
            )}

            {status === "ok" && (
              <>
                <CheckCircle2
                  className="mx-auto h-10 w-10 text-emerald-600"
                  data-testid="status-ok-icon"
                />
                <h1 className="text-xl font-semibold" data-testid="status-ok">
                  Payment received
                </h1>
                <p className="text-sm text-stone-600">
                  Your course is now unlocked.
                  {payment && (
                    <>
                      {" "}
                      Reference:{" "}
                      <code className="text-xs">{payment.id.slice(0, 8)}</code>
                    </>
                  )}
                </p>
                <Link to="/student">
                  <Button
                    className="bg-stone-900 hover:bg-stone-800 text-white"
                    data-testid="back-to-dashboard"
                  >
                    Go to my dashboard <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}

            {status === "fail" && (
              <>
                <XCircle
                  className="mx-auto h-10 w-10 text-red-600"
                  data-testid="status-fail-icon"
                />
                <h1 className="text-xl font-semibold" data-testid="status-fail">
                  Payment failed
                </h1>
                <p className="text-sm text-stone-600">
                  Your card was not charged. Please try again or contact admin.
                </p>
                <Link to="/student">
                  <Button variant="outline">Back to dashboard</Button>
                </Link>
              </>
            )}

            {status === "cancelled" && (
              <>
                <AlertCircle
                  className="mx-auto h-10 w-10 text-amber-600"
                  data-testid="status-cancelled-icon"
                />
                <h1
                  className="text-xl font-semibold"
                  data-testid="status-cancelled"
                >
                  Payment cancelled
                </h1>
                <p className="text-sm text-stone-600">
                  You cancelled the checkout. You can try again from your
                  dashboard anytime.
                </p>
                <Link to="/student">
                  <Button variant="outline">Back to dashboard</Button>
                </Link>
              </>
            )}

            {status === "timeout" && (
              <>
                <AlertCircle
                  className="mx-auto h-10 w-10 text-amber-600"
                  data-testid="status-timeout-icon"
                />
                <h1 className="text-xl font-semibold" data-testid="status-timeout">
                  Still waiting…
                </h1>
                <p className="text-sm text-stone-600">
                  Confirmation is taking longer than usual. Your dashboard will
                  update once the webhook arrives.
                </p>
                <Link to="/student">
                  <Button variant="outline">Back to dashboard</Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
